import { Lodash } from 'lang-utils'
import { ServiceClient } from 'src/utils'
import * as lang from 'src/utils/lang'

const lodash = Lodash.singleton
const serviceClient = ServiceClient.singleton

const UF_MAP = newUFMap()

export enum MatrizOrFilial {
    matriz = 1,
    filial = 2
}

export enum SituacaoCadastral {
    baixada = 8,
    ativa = 2,
    inapta = 4,
    supensa = 3,
    nula = 1
}

export type DateRange = Partial<{ from: string; to: string; mode: JunctionMode }>
export type ValueRange = Partial<{
    gt: number
    gte: number
    lt: number
    lte: number
    from: number
    to: number
}>

export type GeoLocation = { lat: number; lon: number }
export type GeoLocationMode = 'polygon' | 'route' | 'circle' | 'map'
export type GeoCircleLocation = { lat: number; lon: number; radius: number }

export type LocalizacaoFilter = Partial<{
    size: number
    polygons: GeoLocation[][]
    routes: GeoLocation[][]
    circles: GeoLocation[][]
    ufs: string[][]
}>

enum Operators {
    contains = 1,
    does_not_contain = 2, // NEG
    starts_with = 3,
    does_not_start_with = 4, // NEG
    is = 5,
    is_not = 6, // NEG
    is_blank = 7,
    is_not_blank = 8, // NEG
    is_empty = 9,
    is_not_empty = 10, // NEG
    is_in = 11,
    is_not_in = 12, // NEG
    is_less_then = 13,
    is_less_then_or_equal_to = 15,
    is_greater_then = 17,
    is_greater_then_or_equal_to = 19,
    in_range = 21,
    out_of_range = 23
}

export enum StringOperator {
    contains = Operators.contains,
    does_not_contain = Operators.does_not_contain,
    starts_with = Operators.starts_with,
    does_not_start_with = Operators.does_not_start_with,
    is = Operators.is,
    is_not = Operators.is_not,
    is_blank = Operators.is_blank,
    is_not_blank = Operators.is_not_blank,
    is_empty = Operators.is_empty,
    is_not_empty = Operators.is_not_empty,
    is_in = Operators.is_in,
    is_not_in = Operators.is_not_in
}

export enum StringKeywordOperator {
    contains = Operators.contains,
    does_not_contain = Operators.does_not_contain,
    starts_with = Operators.starts_with,
    does_not_start_with = Operators.does_not_start_with,
    is = Operators.is,
    is_not = Operators.is_not,
    is_blank = Operators.is_blank,
    is_not_blank = Operators.is_not_blank,
    is_in = Operators.is_in,
    is_not_in = Operators.is_not_in
}

export enum SelectOperator {
    is = Operators.is,
    is_not = Operators.is_not,
    is_blank = Operators.is_blank,
    is_not_blank = Operators.is_not_blank
}

export enum NumberOperator {
    is_less_then = Operators.is_less_then,
    lt = Operators.is_less_then,
    is_less_then_or_equal_to = Operators.is_less_then_or_equal_to,
    lte = Operators.is_less_then_or_equal_to,
    is_greater_then = Operators.is_greater_then,
    gt = Operators.is_greater_then,
    is_greater_then_or_equal_to = Operators.is_greater_then_or_equal_to,
    gte = Operators.is_greater_then_or_equal_to,
    is = Operators.is,
    is_not = Operators.is_not,
    is_blank = Operators.is_blank,
    is_not_blank = Operators.is_not_blank,
    in_range = Operators.in_range,
    out_of_range = Operators.out_of_range
}

export enum DateOperator {
    in_range = Operators.in_range
}

export enum JunctionMode {
    AND = 1,
    OR = 2
}

export type StringFilter = {
    mode: JunctionMode
    operator: StringOperator
    value?: string
}

export type StringKeywordFilter = {
    mode: JunctionMode
    operator: StringKeywordOperator
    value?: string
}

export type MultipleStringKeywordFilter = {
    mode: JunctionMode
    operator: StringKeywordOperator
    value?: string[]
}

export type SelectFilter = {
    mode: JunctionMode
    operator: SelectOperator
    value?: string
}

export type EmpresaCodeFilter = StringKeywordFilter & {
    cloning: boolean
}

export type NumberFilter = {
    mode: JunctionMode
    operator: NumberOperator
    value?: string | number
    to?: string | number
    toExclusive?: boolean
}

export type ColumnSort = {
    id: string
    desc: boolean
}

export type ColumnFilter = {
    id: string
    value: unknown
}

export type CboCodeFilter = MultipleStringKeywordFilter & {
    isFilterQuantidade?: boolean
    extraMode?: JunctionMode
    extraOperator?: NumberOperator
    extraValue?: string | number
    extraTo?: string | number
}

export type TipoAtividadeEconomicaCodeFilter = MultipleStringKeywordFilter & {
    domain?: number
}

export type TipoAtividadeEconomicaTextFilter = StringFilter & {
    domain?: number
}

export type EmpresaFilter = Partial<{
    situacaoCadastral: SituacaoCadastral[]
    matrizOrFilial: MatrizOrFilial[]
    localizacao: LocalizacaoFilter

    empresaRazaoSocial: StringFilter[]
    empresaNomeFantasia: StringFilter[]
    empresaCodigo: StringKeywordFilter[]
    empresaCnpjMatriz: StringKeywordFilter[]
    empresaCnpjGrupo: StringKeywordFilter[]
    empresaNomeMatriz: StringFilter[]

    atividadeEconomicaCodes: StringKeywordFilter[]
    atividadeEconomicaTexts: StringFilter[]
    naturezaJuridicaGrupoCodes: MultipleStringKeywordFilter[]
    naturezaJuridicaPublicaPrivadaCodes: SelectFilter[]

    tipoEstabelecimentoSaudeCodes: TipoAtividadeEconomicaCodeFilter[]
    porteEstabelecimentoSaudeCodes: TipoAtividadeEconomicaCodeFilter[]
    tipoAtividadeEconomicaCodes: TipoAtividadeEconomicaCodeFilter[]
    tipoAtividadeEconomicaTexts: TipoAtividadeEconomicaTextFilter[]
    cnaeClasseCodes: TipoAtividadeEconomicaCodeFilter[]
    cnaeClasseTexts: TipoAtividadeEconomicaTextFilter[]
    cnaeDivisaoCodes: TipoAtividadeEconomicaCodeFilter[]
    cnaeDivisaoTexts: TipoAtividadeEconomicaTextFilter[]
    cnaeGrupoCodes: TipoAtividadeEconomicaCodeFilter[]
    cnaeGrupoTexts: TipoAtividadeEconomicaTextFilter[]
    cnaeSecaoCodes: TipoAtividadeEconomicaCodeFilter[]
    cnaeSecaoTexts: TipoAtividadeEconomicaTextFilter[]
    cnaeSetorCods: TipoAtividadeEconomicaCodeFilter[]

    empresaCnes: StringKeywordFilter[]
    empresaGrupoCodes: StringKeywordFilter[]

    enderecoCodes: StringKeywordFilter[]
    enderecoTexts: StringFilter[]

    enderecoRuaCodes: StringKeywordFilter[]
    enderecoRuaTexts: StringFilter[]

    enderecoBairroCodes: StringKeywordFilter[]
    enderecoBairroTexts: StringFilter[]

    enderecoCidadeCodes: MultipleStringKeywordFilter[]
    enderecoCidadeTexts: StringFilter[]

    cboCodes: MultipleStringKeywordFilter[]
    cboTexts: StringFilter[]
    cboCategorias: MultipleStringKeywordFilter[]

    pesCboCodes: MultipleStringKeywordFilter[]
    pesCboTexts: StringFilter[]
    pesCboCategorias: MultipleStringKeywordFilter[]

    enderecoUfs: MultipleStringKeywordFilter[]
    enderecoUfTexts: StringFilter[]

    enderecoCeps: StringKeywordFilter[]
    enderecoCepTexts: StringFilter[]

    enderecoRegiaoMetropolitanaCodes: MultipleStringKeywordFilter[]
    enderecoRegiaoMetropolitanaTexts: StringFilter[]

    naturezaJuridicaCodes: MultipleStringKeywordFilter[]
    naturezaJuridicaTexts: StringFilter[]
    naturezaJuridicaCodeList: MultipleStringKeywordFilter[]

    scoreRiscos: MultipleStringKeywordFilter[]

    inicioAtividade: DateRange[]
    dtSituacaoCadastral: DateRange[]
    dtCompetenciaInicioAtividade: DateRange[]
    idadeEmpresaEmAnos: NumberFilter[]
    totalEmpresasNoGrupo: NumberFilter[]
    capitalSocial: NumberFilter[]
    totalFuncionarios: NumberFilter[]
    totalFuncionarios_0_a_18: NumberFilter[]
    totalFuncionarios_19_a_33: NumberFilter[]
    totalFuncionarios_34_a_48: NumberFilter[]
    totalFuncionarios_49_a_58: NumberFilter[]
    totalFuncionarios_59_ou_mais: NumberFilter[]
    totalFuncionariosFemininos: NumberFilter[]
    totalFuncionariosMasculinos: NumberFilter[]
    totalFuncionariosDoGrupo: NumberFilter[]
    percentualFuncionariasMulheres: NumberFilter[]
    faturamentoEstimado: NumberFilter[]
    matrizFilial: SelectFilter[]
    regimeTributario: MultipleStringKeywordFilter[]
    porteReceitaFederal: MultipleStringKeywordFilter[]
    regiaoGeograficaImediataCodes: MultipleStringKeywordFilter[]
    regiaoGeograficaIntermediariaCodes: MultipleStringKeywordFilter[]
    portePorFuncionarios: MultipleStringKeywordFilter[]
    portePorFaturamentoEstimadoCnpj: MultipleStringKeywordFilter[]
    portePorFaturamentoEstimadoGrupo: MultipleStringKeywordFilter[]
    cadeiaValorSaude: SelectFilter[]
    faturamentoEstimadoGrupo: NumberFilter[]

    // colunas da listagem de empresas que não tinham filtro
    matrizUfs: MultipleStringKeywordFilter[]
    enderecoNumeros: StringKeywordFilter[]
    enderecoComplementoTexts: StringFilter[]
    enderecoLatitude: NumberFilter[]
    enderecoLongitude: NumberFilter[]
    telefone1: StringFilter[]
    telefone2: StringFilter[]
    email: StringFilter[]
    totalFiliais: NumberFilter[]
    totalFuncionariosMatrizEFiliais: NumberFilter[]
    salarioTotal: NumberFilter[]
    salarioTotalGrupo: NumberFilter[]
    optanteSimples: MultipleStringKeywordFilter[]
    optanteMei: MultipleStringKeywordFilter[]
    webNomeTexts: StringFilter[]
    webCategoriasTexts: StringFilter[]
    webNota: NumberFilter[]
    webQtdAvaliacoes: NumberFilter[]
    webTempoMedio: NumberFilter[]
    webSite: StringFilter[]
    webTelefone: StringFilter[]

    socioKey: StringKeywordFilter[]
    socioCodigo: StringKeywordFilter[]
    socioNome: StringFilter[]

    prfCodigo: StringKeywordFilter[]
    prfNome: StringFilter[]

    pesCodigo: StringKeywordFilter[]
    pesNome: StringFilter[]

    enchenteRs2024: boolean
}>

export type FetchRequest = Partial<{
    sobre_os_dados: boolean | object
    total_de_funcionarios_por_faixa_funcionario_grupo: boolean | object
    total_de_empresas_por_faixa_de_faturamento_anual: boolean | object
    total_de_empresas_por_faixa_de_faturamento_consolidado_grupo: boolean | object
    total_de_empresas: boolean | object
    total_de_empresas_ativas: boolean | object
    total_de_empresas_ativas_por_uf: boolean | object
    total_de_empresas_matrizes: boolean | object
    total_de_empresas_filiais: boolean | object
    total_novas_empresas_ultimo_mes: boolean | object
    total_de_empresas_no_grupo: boolean | object
    total_de_empresas_por_faixa_de_capital_social: boolean | object
    total_de_empresas_por_faixa_de_idade: boolean | object
    total_de_empresas_por_faixa_de_funionarios: boolean | object
    total_de_empresas_por_faixa_de_funionarios_grupo: boolean | object
    quantidade_de_novas_empresas_abertas_por_mes: boolean | object
    quantidade_de_novas_empresas_fechadas_por_mes: boolean | object
    ativ_economica_com_mais_empresas_funcionarios: boolean | object
    total_por_situacao_cadastral: boolean | object
    total_por_natureza_jurica: boolean | object
    listagem_localizacao: boolean | object
    listagem_empresas: boolean | object
    listagem_prf: boolean | object
    listagem_qsa: boolean | object
    listagem_pes: boolean | object

    filter?: EmpresaFilter
    compliance?: boolean
}>

export type SobreOsDados = {
    dataCorte: string
    /** Ausente quando o servidor não conseguiu ler a data de criação do índice. */
    atualizadoEm?: string
}

export type AggTotal = {
    value: number
}

export type AggValueRange = {
    buckets: {
        key: string
        from?: number
        to?: number
        doc_count: number
    }[]
}

export type AggDateRange = {
    buckets: {
        key_as_string: string
        key: number
        from?: number
        to?: number
        doc_count: number
    }[]
}

export type AggTopCNAE = {
    doc_count_error_upper_bound: number
    sum_other_doc_count: number
    buckets: {
        key: string
        doc_count: number
        total_empregados: AggTotal
        total_empresas: AggTotal
        description: {
            secao: string
            divisao: string
            grupo: string
            classe: string
            subclasse: string
        }
    }[]
}

export type AggTerm<T> = {
    doc_count_error_upper_bound: number
    sum_other_doc_count: number
    buckets: {
        key: T
        doc_count: number
    }[]
}

export type AggTermWithDescripton<T> = {
    doc_count_error_upper_bound: number
    sum_other_doc_count: number
    buckets: {
        key: T
        doc_count: T
        description: string
    }[]
}

export type EmpresaBean = {
    score: number
    cnpj: string
    cnes?: string
    nome: string
    dta_inicio_atividade: string | Date
    cnae: string
    cnae_descricao: string
    natureza_juridica?: string
    natureza_juridica_grp?: number
    situacao_cadastral_nme?: string
    dta_situacao_cadastral?: string | Date
    endereco_lat?: number
    endereco_lon?: number
    endereco_cep?: string
    endereco_rua_id?: number
    endereco_rua_nome?: string
    endereco_numero?: string
    endereco_complemento?: string
    endereco_bairro_id?: number
    endereco_bairro_nome?: string
    endereco_cidade_id?: number
    endereco_cidade_nome?: string
    endereco_uf?: string
    endereco_uf_nome?: string
    regiao_metropolitana?: string
    regiao_geo_imediata?: string
    regiao_geo_intermediaria?: string
    socios_nomes?: string
    func_qtd?: number
    func_qtd_masc?: number
    func_qtd_fem?: number
    func_qtd_0_a_18?: number
    func_qtd_19_a_33?: number
    func_qtd_34_a_48?: number
    func_qtd_49_a_58?: number
    func_qtd_59_ou_mais?: number
    func_qtd_matriz_e_filiais?: number
    faturamento_estimado?: number
    faturamento_estimado_grupo?: number
    emp_idade_anos?: number
    emp_qtd_grupo?: number
    emp_captial_social?: number

    matriz_cnpj?: string
    matriz_nome?: string
    matriz_uf?: string
    telefone1?: string
    telefone2?: string
    email?: string
    salario_total?: number
    salario_total_grupo?: number
    qtd_filiais?: unknown
    web_nome?: string
    web_categoria?: string
    web_nota?: number
    web_qtd_avaliacoes?: number
    web_site?: string
    web_tel?: string
    web_tempo_medio?: number
    optante_simples?: string
    porte_codigo?: number
    porte_funcionarios?: number
    porte_fat_cnpj?: number
    porte_fat_grupo?: number
    porte_leitos?: string
    regime_tributario?: string | number
    score_risco?: string
    cadeia_valor_saude?: number
    tipo_estabelecimento_saude?: string
}

export type EmpresaAggBean = {
    'pj-count': {
        value: number
    }
    'pj-fatu-est-emp-sum': {
        value: number
    }
    'pj-func-qtd-emp-sum': {
        value: number
    }
    'pj-fatu-est-grp-sum': {
        value: number
    }
}

export type EmpresaLocationBean = {
    cnpj: string
    nome: string
    endereco_location?: {
        lat: number
        lon: number
    }
}

// QSA - Sócios

export type QSABean = {
    key: string
    cnpj: string
    empresa_razao: string
    empresa_nome_fantasia: string
    socio_id: string
    socio_nome: string
    socio_cpf_or_cnpj: string
    entrada_sociedade_dta: string | Date
    qualificacao: string
    endereco?: string
    lnkin_url?: string
    lnkin_titulo?: string
    lnkin_perfil?: string
    lnkin_duracao?: string
    instgr_url?: string
    instgr_user?: string
    instgr_qtd_followers?: number
    instgr_qtd_following?: number
}

export type QSAAggBean = {
    'qsa-count': {
        value: number
    }
}

// PRF - Médicos

export type PRFBean = {
    key: string
    ipf: string
    cpf: string
    nome: string
    cnpj: string
    pj_nome: string
    sexo: string
    dt_nascimento: string
    celular: string
    endereco: {
        id: number
        rua: {
            id: number
            ds: string
        }
        bairro: {
            id: number
            ds: string
        }
        cidade: {
            id: number
            ds: string
        }
        uf: {
            cd: number
        }
        cep: string
        numero: string
        complemento: string
    }
    crm: {
        cd: string
        uf: {
            cd: string
            ds: string
        }
        inscricao: {
            cd: string
            data: string
            data_primeira: string
        }
        situacao: {
            co: string
            ds: string
        }
        telefone: string[]
        endereco: string
        contato_autorizado: boolean
    }
    fl_eh_socio: boolean
    socio_qualificacao: {
        cod: string
        dsc: string
    }[]
    cbo: {
        cod: string
        dsc: string
    }[]
}

export type PRFRow = {
    key: string
    ipf: string
    cpf: string
    nome: string
    cnpj: string
    pj_nome: string
    sexo: string
    dt_nascimento?: Date
    celular: string
    crm: string
    crm_inscricao_tipo: string
    crm_inscricao_data?: Date
    crm_inscricao_data_primeira?: Date
    crm_situacao: string
    crm_telefones: string
    crm_endereco: string
    socio_qualificacoes: string
    cbos: string
    selected?: boolean
}

export type PRFAggBean = {
    'prf-count': {
        value: number
    }
}

// Pessoa - Empregados

export type PESBean = {
    key: string
    ipf: string
    cpf: string
    nome: string
    cnpj: string
    pj_nome: string
    sexo: string
    dt_nascimento: string
    celular: string
    dt_admissao: string
    endereco: {
        id: number
        rua: {
            id: number
            ds: string
        }
        bairro: {
            id: number
            ds: string
        }
        cidade: {
            id: number
            ds: string
        }
        uf: {
            cd: number
        }
        cep: string
        numero: string
        complemento: string
    }
    cbo: {
        cod: string
        dsc: string
    }[]
    linkedin: {
        url: string
        titulo: string
        descricao: string
        duracao_em_anos: number
        duracao_em_meses: number
        eh_ceo: boolean
        eh_comercial: boolean
        eh_compras: boolean
        eh_coordenador: boolean
        eh_diretor: boolean
        eh_gerente: boolean
        eh_representante: boolean
    }
    instagram: {
        url: string
        user: string
        qtd_followers: number
        qtd_following: number
    }
}

export type PESRow = {
    key: string
    ipf: string
    cpf: string
    nome: string
    cnpj: string
    pj_nome: string
    sexo: string
    dt_nascimento?: string
    celular: string
    dt_admissao?: Date
    endereco: string
    cbos: string
    lnkin_url?: string
    lnkin_titulo?: string
    lnkin_perfil?: string
    lnkin_duracao?: string
    instgr_url?: string
    instgr_user?: string
    instgr_qtd_followers?: number
    instgr_qtd_following?: number
    selected?: boolean
}

export type PESAggBean = {
    'pes-count': {
        value: number
    }
}

export type FetchResponse = Partial<{
    sobre_os_dados: SobreOsDados
    total_de_funcionarios_por_faixa_funcionario_grupo: AggValueRange
    total_de_empresas_por_faixa_de_faturamento_anual: AggValueRange
    total_de_empresas_por_faixa_de_faturamento_consolidado_grupo: AggValueRange
    total_de_empresas: AggTotal
    total_de_empresas_ativas: AggTotal
    total_de_empresas_ativas_por_uf: AggTerm<string>
    total_de_empresas_matrizes: AggTotal
    total_de_empresas_filiais: AggTotal
    total_novas_empresas_ultimo_mes: AggTotal
    total_de_empresas_no_grupo: AggValueRange
    total_de_empresas_por_faixa_de_capital_social: AggValueRange
    total_de_empresas_por_faixa_de_idade: AggValueRange
    total_de_empresas_por_faixa_de_funionarios: AggValueRange
    total_de_empresas_por_faixa_de_funionarios_grupo: AggValueRange
    quantidade_de_novas_empresas_abertas_por_mes: AggDateRange
    quantidade_de_novas_empresas_fechadas_por_mes: AggDateRange
    ativ_economica_com_mais_empresas_funcionarios: AggTopCNAE
    total_por_situacao_cadastral: AggTerm<number>
    total_por_natureza_jurica: AggTermWithDescripton<number>
    listagem_localizacao: EmpresaLocationBean[]
    listagem_empresas: EmpresaBean[]
    listagem_empresas_agg: EmpresaAggBean
    listagem_qsa: QSABean[]
    listagem_qsa_agg: QSAAggBean
    listagem_prf: PRFBean[]
    listagem_prf_agg: PRFAggBean
    listagem_pes: PESBean[]
    listagem_pes_agg: PESAggBean
}>

export type AtividadeEconomicaSubclasse = {
    'pj-ae-subclasse-cod': string
    'pj-ae-secao-cod': string
    'pj-ae-secao-dsc': string
    'pj-ae-divisao-cod': string
    'pj-ae-divisao-dsc': string
    'pj-ae-classe-cod': string
    'pj-ae-classe-dsc': string
    'pj-ae-subclasse-dsc': string
    'pj-ae-grupo-cod': string
    'pj-ae-grupo-dsc': string
}

export type Empresa = {
    key: string
    source: Partial<{
        'pj-addr-id': number
        'pj-fl-matriz': number
        'pj-addr-regiao-metropolitana-cod': number
        'pj-media-crescimento-2021_2022': number
        'pj-cnpj-grupo': string
        'pj-emp-grp-qtd': number
        'pj-emp-idade-anos': number
        'pj-ae-secao-dsc': string
        'pj-addr-rua-nme': string
        'pj-addr-bairro-nme': string
        'pj-ae-divisao-cod': string
        'pj-rs-enchente-2024-05-02': number
        'pj-razao-social': string
        'pj-score-risco-code': string
        'pj-addr-cep': string
        'pj-rs-enchente-2024-05-06': number
        'pj-addr-cidade-id': number
        'pj-addr-rua-id': number
        'pj-cadastrao-sit-dta': string
        'pj-cnpj-matriz': string
        'pj-crescimento-2022': number
        'pj-addr-uf-dsc': string
        'pj-crescimento-2021': number
        'pj-ae-classe-cod': string
        'pj-addr-rua-compl': string
        'pj-ae-grupo-cod': string
        'pj-nat-jur-cod': number
        'pj-addr-uf': string
        'pj-fatu-est-grp': number
        'pj-nome-fantasia': string
        'pj-matriz-nome': string
        'pj-ae-subclasse-dsc': string
        'pj-addr-bairro-id': number
        'pj-ae-subclasse-cod': string
        'pj-addr-txt': string
        'pj-inicio-atividade-dta': string
        'pj-nat-jur-nme': string
        'pj-func-qtd-grp': number
        'pj-ae-secao-cod': 'S'
        'pj-addr-regiao-metropolitana-dsc': string
        'pj-fatu-est-emp': number
        'pj-cnpj': string
        'pj-addr-cidade-nme': string
        'pj-ae-divisao-dsc': string
        'pj-competencia-inicio-ativ-dta': string
        'pj-func-qtd-emp': number
        'pj-addr-location': {
            lat: number
            lon: number
        }
        'pj-cadastrao-sit': number
        'pj-ae-classe-dsc': string
        'pj-emp-cap-social': number
        'pj-ae-grupo-dsc': string
    }>
}

export type QuatroSocietario = {
    key: string
    source: Partial<{
        qsa_key: string
        qsa_cnpj: string
        qsa_socio: {
            id: string
            cpf: string
            nome: string
        }
        qsa_addr: {
            id: number
            rua: {
                id: number
                nme: string
            }
            bairro: {
                id: number
                nme: string
            }
            cidade: {
                id: number
                nme: string
            }
            uf: {
                cd: string
                dsc: string
            }
            cep: string
            location: {
                lat: number
                lon: number
            }
            numero: string
            txt: string
        }
        qsa_qualificacao: {
            cod: string
            dsc: string
        }
        qsa_entrada_sociedade_dta: string
        qsa_situacao_cadastral: number
    }>
}

export type ProfissionalSaude = {
    key: string
    source: Partial<{
        prf_id: string
        prf_cpf: string
        prf_nome: string
    }>
}

export type Empregado = {
    key: string
    source: Partial<{
        pes_id: string
        pes_cpf: string
        pes_nome: string
    }>
}

export type UnidadeFederacao = {
    key: string
    source: {
        'pj-addr-uf': string
        'pj-addr-uf-dsc': string
        'pj-addr-location'?: {
            lat: number
            lon: number
        }
    }
}

export type Cidade = {
    key: number
    source: {
        'pj-addr-cidade-id': number
        'pj-addr-cidade-nme': string
        'pj-addr-uf': string
        'pj-addr-uf-dsc': string
        'pj-addr-location'?: {
            lat: number
            lon: number
        }
    }
}

export type Bairro = {
    key: number
    source: {
        'pj-addr-bairro-id': number
        'pj-addr-bairro-nme': string
    }
}

// Valores dos filtros de lista lidos do índice (POST /api/mercado-consumidor/dominio-pj).
export type DominioPj =
    'regiao-metropolitana' | 'natureza-juridica' | 'regiao-geo-imediata' | 'regiao-geo-intermediaria'

export type DominioPjOpcao = {
    key: string
    label: string
    uf?: string
    total: number
}

export type RegiaoMetropolitana = {
    key: number
    source: {
        'pj-addr-regiao-metropolitana-cod': number
        'pj-addr-regiao-metropolitana-dsc': string
    }
}

export type NaturezaJuridica = {
    key: number
    source: {
        'pj-nat-jur-cod': number
        'pj-nat-jur-nme': string
    }
}

export type EmpresaFieldProjection = (keyof Empresa['source'])[]

export type FilterSelectOption = {
    id: string
    title: string
}

export type FilterCatalogOption = {
    id: string
    owner: boolean
    ownerId: number
    title: string
    group: string
    groups: number[]
    users: number[]
    favorite: boolean
    fieldCount: number
    fieldKeys: string[]
    sharedCount: number
    ownerName?: string
    updatedAt?: string
}

export type SaveFilterRequest = {
    id?: string
    module?: string
    title: string
    group: string
    groups: number[]
    users: number[]
    data: EmpresaFilter
}

export type RenameFilterRequest = {
    id: string
    title: string
    group: string
}

export type ShareFilterRequest = {
    id: string
    groups: number[]
    users: number[]
}

export type AlreadyExportedDocument = {
    docType: string
    docId: string
    docLabel?: string
    dataHora: string
    origem: string
    userName: string
    groupName?: string
}

export type ExportDataStatus = {
    canExport: boolean
    unlimitedDataExport: boolean
    message: string
    limitExportData?: number
    currentMonthExportedData?: number
    requestExportQtdd?: number
    reportType: 'empresas' | 'socios' | 'medicos' | 'vinculos' | undefined
    groupId?: number | null
    blockExportWhenExceeded?: boolean
    alreadyExported?: AlreadyExportedDocument[]
}

export type CBO = {
    cod: string
    dsc: string
}

export type CrmDirection = 'inbound' | 'outbound' | 'both'

export type CrmConnectionOption = {
    id: number
    group_id: number
    crm_type: string
    name: string
    direction: CrmDirection
    active: boolean
}

export type PushQueuedResult = { kind: 'queued'; jobId: number; deduped: boolean }

export type PushFranchiseExceededResult = { kind: 'franchise_exceeded'; franchise: ExportDataStatus }

export type PushResult = PushQueuedResult | PushFranchiseExceededResult

export class TheConsumerMarketService {
    private static INSTANCE = new TheConsumerMarketService()

    static singleton() {
        return TheConsumerMarketService.INSTANCE
    }

    async fetch(request: FetchRequest) {
        return serviceClient().authPost<FetchResponse>('/api/mercado-consumidor/fetch', request)
    }

    async fetchSobreOsDados() {
        const resp = await this.fetch({
            sobre_os_dados: true
        })
        return resp.sobre_os_dados!
    }

    async downloadXlsx(
        filter: EmpresaFilter,
        columns: string[],
        reportType: 'empresas' | 'socios' | 'medicos' | 'vinculos',
        qttRecordsToDownload?: number,
        compliance = false,
        excludeDocIds?: string[]
    ) {
        const response = await serviceClient().authRawPost(`/api/mercado-consumidor/xlsx/${reportType}`, {
            filters: filter,
            columns: columns,
            qttRecordsToDownload: qttRecordsToDownload || 0,
            compliance,
            excludeDocIds: excludeDocIds ?? []
        })
        if (response.status !== 201 && response.status !== 200) {
            const body = await response.text()
            if (body.charAt(0) === '{') {
                const errorInfo = JSON.parse(body) as { message: string }
                throw new Error(`${response.status}: ${errorInfo.message ?? response.statusText}`)
            }
            throw new Error(`${response.status}: ${response.statusText}`)
        }

        return response.blob()
    }

    async checkLimitExportData(filter: EmpresaFilter, reportType: 'empresas' | 'socios' | 'medicos' | 'vinculos') {
        const response = await serviceClient().authRawPost(`/api/mercado-consumidor/xlsx/${reportType}/check-limit`, {
            filters: filter
        })
        if (response.status !== 201 && response.status !== 200) {
            const body = await response.text()
            if (body.charAt(0) === '{') {
                const errorInfo = JSON.parse(body) as { message: string }
                throw new Error(`${response.status}: ${errorInfo.message ?? response.statusText}`)
            }
            throw new Error(`${response.status}: ${response.statusText}`)
        }

        return response.json() as Promise<ExportDataStatus>
    }

    async searchEmpresaByRazaoSocial(
        text: string,
        filterContext?: EmpresaFilter,
        fields?: (keyof Empresa['source'])[]
    ): Promise<Empresa[]> {
        const request: EmpresaFilter = filterContext ? lodash().cloneDeep(filterContext) : {}

        const cnpjArray: string[] = []
        const textArray: string[] = []

        const searchParts = text.split(/[,;]|\s/)
        for (const searchPart of searchParts) {
            const cnpj = lang.onlyNumbers(searchPart)
            if (!cnpj) {
                searchPart.length > 0 && textArray.push(searchPart)
                continue
            }

            if (cnpj.length === 14) {
                cnpjArray.push(cnpj)
                continue
            }

            searchPart.length > 0 && textArray.push(searchPart)
        }

        request.empresaCodigo = []
        request.empresaRazaoSocial = []

        let hasFilter = false
        if (textArray.length > 0) {
            request.empresaRazaoSocial.push({
                mode: JunctionMode.OR,
                operator: StringOperator.contains,
                value: textArray.join(' ')
            })
            hasFilter = true
        }

        if (cnpjArray.length > 0) {
            request.empresaCodigo.push({
                mode: JunctionMode.OR,
                operator: StringKeywordOperator.is_in,
                value: cnpjArray.join(',')
            })
            hasFilter = true
        }

        if (!hasFilter) {
            return []
        }

        type EmpresaResponse = {
            buckets: Empresa[]
        }

        if (!fields || fields.length === 0) {
            fields = ['pj-razao-social']
        }

        const response = await serviceClient().authPost<EmpresaResponse>('/api/mercado-consumidor/search-empresa', {
            ...request,
            fields
        })

        return response.buckets
    }

    async searchEmpresaByNomeFantasia(
        text: string,
        filterContext?: EmpresaFilter,
        fields?: (keyof Empresa['source'])[]
    ): Promise<Empresa[]> {
        const request: EmpresaFilter = filterContext ? lodash().cloneDeep(filterContext) : {}

        const cnpjArray: string[] = []
        const textArray: string[] = []

        const searchParts = text.split(/[,;]|\s/)
        for (const searchPart of searchParts) {
            const cnpj = lang.onlyNumbers(searchPart)
            if (!cnpj) {
                searchPart.length > 0 && textArray.push(searchPart)
                continue
            }

            if (cnpj.length === 14) {
                cnpjArray.push(cnpj)
                continue
            }

            searchPart.length > 0 && textArray.push(searchPart)
        }

        request.empresaCodigo = []
        request.empresaNomeFantasia = []

        let hasFilter = false
        if (textArray.length > 0) {
            request.empresaNomeFantasia.push({
                mode: JunctionMode.OR,
                operator: StringOperator.contains,
                value: textArray.join(' ')
            })
            hasFilter = true
        }

        if (cnpjArray.length > 0) {
            request.empresaCodigo.push({
                mode: JunctionMode.OR,
                operator: StringKeywordOperator.is_in,
                value: cnpjArray.join(',')
            })
            hasFilter = true
        }

        if (!hasFilter) {
            return []
        }

        type EmpresaResponse = {
            buckets: Empresa[]
        }

        if (!fields || fields.length === 0) {
            fields = ['pj-nome-fantasia']
        }

        const response = await serviceClient().authPost<EmpresaResponse>('/api/mercado-consumidor/search-empresa', {
            ...request,
            fields
        })

        return response.buckets
    }

    async searchMatrizByNome(
        text: string,
        filterContext?: EmpresaFilter,
        fields?: (keyof Empresa['source'])[]
    ): Promise<Empresa[]> {
        const request: EmpresaFilter = filterContext ? lodash().cloneDeep(filterContext) : {}

        const cnpjArray: string[] = []
        const textArray: string[] = []

        const searchParts = text.split(/[,;]|\s/)
        for (const searchPart of searchParts) {
            const cnpj = lang.onlyNumbers(searchPart)
            if (!cnpj) {
                searchPart.length > 0 && textArray.push(searchPart)
                continue
            }

            if (cnpj.length === 14) {
                cnpjArray.push(cnpj)
                continue
            }

            searchPart.length > 0 && textArray.push(searchPart)
        }

        request.empresaCnpjMatriz = []
        request.empresaNomeMatriz = []

        let hasFilter = false
        if (textArray.length > 0) {
            request.empresaNomeMatriz.push({
                mode: JunctionMode.OR,
                operator: StringOperator.contains,
                value: textArray.join(' ')
            })
            hasFilter = true
        }

        if (cnpjArray.length > 0) {
            request.empresaCnpjMatriz.push({
                mode: JunctionMode.OR,
                operator: StringKeywordOperator.is_in,
                value: cnpjArray.join(',')
            })
            hasFilter = true
        }

        if (!hasFilter) {
            return []
        }

        type EmpresaResponse = {
            buckets: Empresa[]
        }

        if (!fields || fields.length === 0) {
            fields = ['pj-matriz-nome']
        }

        const response = await serviceClient().authPost<EmpresaResponse>('/api/mercado-consumidor/search-empresa', {
            ...request,
            fields
        })

        return response.buckets
    }

    async searchSocioByNome(
        text: string,
        filterContext?: EmpresaFilter,
        fields?: (keyof QuatroSocietario['source'])[]
    ): Promise<QuatroSocietario[]> {
        const request: EmpresaFilter = filterContext ? lodash().cloneDeep(filterContext) : {}

        const textArray: string[] = lang.splitValues(text)

        request.socioNome = []

        let hasFilter = false
        if (textArray.length > 0) {
            request.socioNome.push({
                mode: JunctionMode.OR,
                operator: StringOperator.contains,
                value: textArray.join(' ')
            })
            hasFilter = true
        }

        if (!hasFilter) {
            return []
        }

        type SocioResponse = {
            buckets: QuatroSocietario[]
        }

        if (!fields || fields.length === 0) {
            fields = ['qsa_socio']
        }

        const response = await serviceClient().authPost<SocioResponse>('/api/mercado-consumidor/search-socio', {
            ...request,
            fields
        })

        return response.buckets
    }

    async searchMedicoByNome(
        text: string,
        filterContext?: EmpresaFilter,
        fields?: (keyof ProfissionalSaude['source'])[]
    ): Promise<ProfissionalSaude[]> {
        const request: EmpresaFilter = filterContext ? lodash().cloneDeep(filterContext) : {}

        const textArray: string[] = lang.splitValues(text)

        request.prfNome = []

        let hasFilter = false
        if (textArray.length > 0) {
            request.prfNome.push({
                mode: JunctionMode.OR,
                operator: StringOperator.contains,
                value: textArray.join(' ')
            })
            hasFilter = true
        }

        if (!hasFilter) {
            return []
        }

        type SocioResponse = {
            buckets: ProfissionalSaude[]
        }

        if (!fields || fields.length === 0) {
            fields = ['prf_nome']
        }

        const response = await serviceClient().authPost<SocioResponse>('/api/mercado-consumidor/search-prf', {
            ...request,
            fields
        })

        return response.buckets
    }

    async searchEmpregadoByNome(
        text: string,
        filterContext?: EmpresaFilter,
        fields?: (keyof Empregado['source'])[]
    ): Promise<Empregado[]> {
        const request: EmpresaFilter = filterContext ? lodash().cloneDeep(filterContext) : {}

        const textArray: string[] = lang.splitValues(text)

        request.pesNome = []

        let hasFilter = false
        if (textArray.length > 0) {
            request.pesNome.push({
                mode: JunctionMode.OR,
                operator: StringOperator.contains,
                value: textArray.join(' ')
            })
            hasFilter = true
        }

        if (!hasFilter) {
            return []
        }

        type SocioResponse = {
            buckets: Empregado[]
        }

        if (!fields || fields.length === 0) {
            fields = ['pes_nome']
        }

        const response = await serviceClient().authPost<SocioResponse>('/api/mercado-consumidor/search-pes', {
            ...request,
            fields
        })

        return response.buckets
    }

    async getAllCnae(filterContext?: EmpresaFilter): Promise<AtividadeEconomicaSubclasse[]> {
        const request: EmpresaFilter = filterContext ? lodash().cloneDeep(filterContext) : {}

        type SearchAESubClasseResponse = {
            buckets: AtividadeEconomicaSubclasse[]
        }

        const response = await serviceClient().authPost<SearchAESubClasseResponse>(
            '/api/mercado-consumidor/search-ae-subclasse',
            request
        )

        return response.buckets
    }

    async getAllCnaeByClasse(filterContext?: EmpresaFilter): Promise<AtividadeEconomicaSubclasse[]> {
        const request: EmpresaFilter = filterContext ? lodash().cloneDeep(filterContext) : {}

        type SearchAESubClasseResponse = {
            buckets: AtividadeEconomicaSubclasse[]
        }

        const response = await serviceClient().authPost<SearchAESubClasseResponse>(
            '/api/mercado-consumidor/search-ae-classe',
            request
        )

        return response.buckets
    }

    async getAllCnaeByDivisao(filterContext?: EmpresaFilter): Promise<AtividadeEconomicaSubclasse[]> {
        const request: EmpresaFilter = filterContext ? lodash().cloneDeep(filterContext) : {}

        type SearchAESubClasseResponse = {
            buckets: AtividadeEconomicaSubclasse[]
        }

        const response = await serviceClient().authPost<SearchAESubClasseResponse>(
            '/api/mercado-consumidor/search-ae-divisao',
            request
        )

        return response.buckets
    }

    async getCnaeByGrupo(filterContext?: EmpresaFilter): Promise<AtividadeEconomicaSubclasse[]> {
        const request: EmpresaFilter = filterContext ? lodash().cloneDeep(filterContext) : {}

        type SearchAESubClasseResponse = {
            buckets: AtividadeEconomicaSubclasse[]
        }

        const response = await serviceClient().authPost<SearchAESubClasseResponse>(
            '/api/mercado-consumidor/search-ae-grupo',
            request
        )

        return response.buckets
    }

    async getCnaeBySecao(filterContext?: EmpresaFilter): Promise<AtividadeEconomicaSubclasse[]> {
        const request: EmpresaFilter = filterContext ? lodash().cloneDeep(filterContext) : {}

        type SearchAESubClasseResponse = {
            buckets: AtividadeEconomicaSubclasse[]
        }

        const response = await serviceClient().authPost<SearchAESubClasseResponse>(
            '/api/mercado-consumidor/search-ae-secao',
            request
        )

        return response.buckets
    }

    async getAllPesCBOs(filterContext?: EmpresaFilter): Promise<CBO[]> {
        const request: EmpresaFilter = filterContext ? lodash().cloneDeep(filterContext) : {}

        type SearchCBOsResponse = {
            buckets: CBO[]
        }

        const response = await serviceClient().authPost<SearchCBOsResponse>(
            '/api/mercado-consumidor/all-cbo-pes',
            request
        )

        return response.buckets
    }

    async getAllCboCategorias(child: 'prf' | 'pes'): Promise<CBO[]> {
        type SearchCboCategoriasResponse = {
            buckets: CBO[]
        }

        const response = await serviceClient().authPost<SearchCboCategoriasResponse>(
            `/api/mercado-consumidor/all-cbo-categoria-${child}`,
            {}
        )

        return response.buckets
    }

    async getAllPrfCBOs(filterContext?: EmpresaFilter): Promise<CBO[]> {
        const request: EmpresaFilter = filterContext ? lodash().cloneDeep(filterContext) : {}

        type SearchCBOsResponse = {
            buckets: CBO[]
        }

        const response = await serviceClient().authPost<SearchCBOsResponse>(
            '/api/mercado-consumidor/all-cbo-prf',
            request
        )

        return response.buckets
    }

    async searchCidadeByDescricao(
        text: string,
        filterContext?: EmpresaFilter,
        fields?: (keyof Cidade['source'])[]
    ): Promise<Cidade[]> {
        const request: EmpresaFilter = filterContext ? lodash().cloneDeep(filterContext) : {}

        request.enderecoCidadeTexts = []

        let hasFilter = false
        if (text.length > 0) {
            request.enderecoCidadeTexts.push({
                mode: JunctionMode.OR,
                operator: StringOperator.contains,
                value: text
            })
            hasFilter = true
        }

        if (!hasFilter) {
            return []
        }

        type CidadeResponse = {
            buckets: Cidade[]
        }

        const response = await serviceClient().authPost<CidadeResponse>(
            '/api/mercado-consumidor/search-endereco-cidade',
            {
                ...request,
                fields
            }
        )

        return response.buckets
    }

    async searchAllCidades(fields?: (keyof Cidade['source'])[], allRecords?: boolean): Promise<Cidade[]> {
        type CidadeResponse = {
            buckets: Cidade[]
        }

        const response = await serviceClient().authPost<CidadeResponse>(
            '/api/mercado-consumidor/search-endereco-cidade',
            {
                fields,
                allRecords
            }
        )

        return response.buckets
    }

    async searchAllUFs(): Promise<UnidadeFederacao[]> {
        const unFedArr = [] as UnidadeFederacao[]
        UF_MAP.forEach((val, key) =>
            unFedArr.push({
                key: key,
                source: {
                    'pj-addr-uf': key,
                    'pj-addr-uf-dsc': val
                }
            })
        )
        return unFedArr
    }

    async searchBairroByDescricao(
        text: string,
        filterContext?: EmpresaFilter,
        fields?: (keyof Bairro['source'])[]
    ): Promise<Bairro[]> {
        const request: EmpresaFilter = filterContext ? lodash().cloneDeep(filterContext) : {}

        request.enderecoBairroTexts = []

        let hasFilter = false
        if (text.length > 0) {
            request.enderecoBairroTexts.push({
                mode: JunctionMode.OR,
                operator: StringOperator.contains,
                value: text
            })
            hasFilter = true
        }

        if (!hasFilter) {
            return []
        }

        type BairroResponse = {
            buckets: Bairro[]
        }

        const response = await serviceClient().authPost<BairroResponse>(
            '/api/mercado-consumidor/search-endereco-bairro',
            {
                ...request,
                fields
            }
        )

        return response.buckets
    }

    async fetchDominioPj(dominio: DominioPj): Promise<DominioPjOpcao[]> {
        type DominioPjResponse = {
            buckets: DominioPjOpcao[]
        }

        const response = await serviceClient().authPost<DominioPjResponse>('/api/mercado-consumidor/dominio-pj', {
            dominio
        })

        return response.buckets
    }

    async fetchSavedFilters(module = 'tcm'): Promise<FilterCatalogOption[]> {
        type FilterFetchResponse = {
            status: number
            options?: FilterCatalogOption[]
            message?: string
        }

        const response = await serviceClient().authPost<FilterFetchResponse>('/api/mercado-consumidor/filter-fetch', {
            module
        })

        if (response.status === 200) {
            return response.options ?? []
        }

        throw new Error(response.message ?? 'Erro interno')
    }

    async saveFilter(req: SaveFilterRequest): Promise<string> {
        type FilterSaveResponse = {
            status: number
            data?: { id: string; rowsAffected: number }
            message?: string
        }

        const response = await serviceClient().authPost<FilterSaveResponse>('/api/mercado-consumidor/filter-save', req)

        if (response.status === 200 && response.data?.id) {
            return response.data.id
        }

        throw new Error(response.message ?? 'Erro interno')
    }

    async renameFilter(req: RenameFilterRequest): Promise<void> {
        type FilterRenameResponse = { status: number; message?: string }

        const response = await serviceClient().authPost<FilterRenameResponse>(
            '/api/mercado-consumidor/filter-rename',
            req
        )

        if (response.status !== 200) {
            throw new Error(response.message ?? 'Erro interno')
        }
    }

    async shareFilter(req: ShareFilterRequest): Promise<void> {
        type FilterShareResponse = { status: number; message?: string }

        const response = await serviceClient().authPost<FilterShareResponse>(
            '/api/mercado-consumidor/filter-share',
            req
        )

        if (response.status !== 200) {
            throw new Error(response.message ?? 'Erro interno')
        }
    }

    async favoriteFilter(id: string, ownerId: number, favorite: boolean): Promise<void> {
        type FilterFavoriteResponse = { status: number; message?: string }

        const response = await serviceClient().authPost<FilterFavoriteResponse>(
            '/api/mercado-consumidor/filter-favorite',
            { id, ownerId, favorite }
        )

        if (response.status !== 200) {
            throw new Error(response.message ?? 'Erro interno')
        }
    }

    async fetchFilterDataById(filterId: string): Promise<EmpresaFilter | null | undefined> {
        type FilterSaveResponse = {
            status: number
            data?: string | null
            message?: string
        }

        const url = `/api/mercado-consumidor/filter-fetch/${filterId}`
        const response = await serviceClient().authGet<FilterSaveResponse>(url)

        if (response.status === 200 && response.data) {
            return JSON.parse(response.data)
        }

        throw new Error(response.message ?? 'Erro interno')
    }

    async deleteFilterById(filterId: string) {
        type FilterDeleteResponse = {
            status: number
            data?: string | null
            message?: string
        }

        const url = `/api/mercado-consumidor/filter-delete/${filterId}`
        const response = await serviceClient().authDelete<FilterDeleteResponse>(url)

        if (response.status === 200 && response.data) {
            return JSON.parse(response.data)
        }

        throw new Error(response.message ?? 'Erro interno')
    }

    async fetchFilterByIA(textualRequest: string): Promise<EmpresaFilter | null | undefined> {
        type FetchFilterByIAResponse = {
            status: number
            data?: { filter: EmpresaFilter; error?: string } | null
            message?: string
        }

        const url = '/api/mercado-consumidor/filter-by-ia'
        const response = await serviceClient().authPostTextBody<FetchFilterByIAResponse>(url, textualRequest)
        if (response.message) {
            throw new Error(response.message)
        }

        if (response.data && response.data.filter) {
            return response.data.filter
        }

        const errorMessage = response.data?.error ?? 'Erro interno'
        if (errorMessage === 'unsupported_request') {
            throw new Error('A IA não conseguiu processar essa requisição')
        }

        throw new Error(errorMessage)
    }

    async fetchPreferredGroupId(): Promise<number | undefined> {
        const profile = await serviceClient().authGet<{ preferred_group_id?: number }>('/api/profile')
        return profile?.preferred_group_id ?? undefined
    }

    async fetchCrmConnections(groupId: number): Promise<CrmConnectionOption[]> {
        const url = `/api/v1/integration/groups/${groupId}/connections`
        const connections = await serviceClient().authGet<CrmConnectionOption[]>(url)
        return (connections ?? []).filter((c) => c.active && (c.direction === 'outbound' || c.direction === 'both'))
    }

    async pushToCrm(req: { cnpjs: string[]; connectionId: number; confirmOverLimit?: boolean }): Promise<PushResult> {
        const response = await serviceClient().authRawPost('/api/v1/integration/push', {
            cnpjs: req.cnpjs,
            connectionId: req.connectionId,
            confirmOverLimit: req.confirmOverLimit ?? false
        })

        if (response.status === 202) {
            const body = (await response.json()) as { jobId: number; deduped?: boolean }
            return { kind: 'queued', jobId: body.jobId, deduped: !!body.deduped }
        }

        if (response.status === 409) {
            const body = (await response.json()) as {
                franchise?: Omit<ExportDataStatus, 'reportType'> & { reportType?: ExportDataStatus['reportType'] }
            }
            const f = body.franchise
            const franchise: ExportDataStatus = {
                canExport: f?.canExport ?? true,
                unlimitedDataExport: f?.unlimitedDataExport ?? false,
                message: f?.message ?? '',
                limitExportData: f?.limitExportData,
                currentMonthExportedData: f?.currentMonthExportedData,
                requestExportQtdd: f?.requestExportQtdd,
                reportType: f?.reportType ?? 'empresas'
            }
            return { kind: 'franchise_exceeded', franchise }
        }

        const bodyText = await response.text()
        if (bodyText.charAt(0) === '{') {
            const errorInfo = JSON.parse(bodyText) as { message?: string }
            throw new Error(`${response.status}: ${errorInfo.message ?? response.statusText}`)
        }
        throw new Error(`${response.status}: ${response.statusText}`)
    }
}

function newUFMap() {
    const map = new Map<string, string>()
    map.set('AC', 'Acre')
    map.set('AL', 'Alagoas')
    map.set('AP', 'Amapá')
    map.set('AM', 'Amazonas')
    map.set('BA', 'Bahia')
    map.set('CE', 'Ceará')
    map.set('DF', 'Distrito Federal')
    map.set('ES', 'Espírito Santo')
    map.set('GO', 'Goiás')
    map.set('MA', 'Maranhão')
    map.set('MT', 'Mato Grosso')
    map.set('MS', 'Mato Grosso do Sul')
    map.set('MG', 'Minas Gerais')
    map.set('PA', 'Pará')
    map.set('PB', 'Paraíba')
    map.set('PR', 'Paraná')
    map.set('PE', 'Pernambuco')
    map.set('PI', 'Piauí')
    map.set('RJ', 'Rio de Janeiro')
    map.set('RN', 'Rio Grande do Norte')
    map.set('RS', 'Rio Grande do Sul')
    map.set('RO', 'Rondônia')
    map.set('RR', 'Roraima')
    map.set('SC', 'Santa Catarina')
    map.set('SP', 'São Paulo')
    map.set('SE', 'Sergipe')
    map.set('TO', 'Tocantins')

    return map
}
